"use client";

import { cn } from "../../lib/utils";
import { User, Bot } from "lucide-react";
import { MessageParts, hasReplyText, isVisibleToolPart } from "./message-parts";

interface MessageBubbleProps {
  role: "user" | "assistant";
  parts: { type: string; [key: string]: unknown }[];
}

/**
 * The colour half of the markdown typography split — applied to the **assistant** bubble only.
 *
 * `MessageParts` renders in both bubbles, so `message-parts.tsx`'s `MARKDOWN_PROSE` carries no colour
 * or fill: on the user bubble (`bg-primary` / `text-primary-foreground`) a `text-primary` link would be
 * `--primary` on `--primary` and a `bg-background` code fill would be white on white. Here the ground
 * is `bg-muted`, where `--primary` measures AA in both themes (ledger `markdown-link-on-bubble`).
 *
 * Every selector is scoped to the `chat-md` marker `MARKDOWN_PROSE` puts on the markdown container.
 * The bare `[&_a]` / `[&_code]` / `[&_pre]` forms would be wrong: `ProductCardGrid` renders as a
 * sibling inside this same bubble, and a descendant rule at specificity (0,1,1) outranks the cards'
 * own single-class utilities — repainting the View Product action `--primary` on `--primary`.
 */
const MARKDOWN_ASSISTANT_COLORS =
  "[&_.chat-md_a]:text-primary [&_.chat-md_code]:bg-background [&_.chat-md_pre]:bg-background";

export function MessageBubble({ role, parts }: MessageBubbleProps) {
  const isUser = role === "user";
  // Hidden tools (logEvent) don't count: a turn that only logged stays a text-width bubble.
  const hasToolParts = parts.some(isVisibleToolPart);

  // An assistant turn with nothing visible yet (no reply text, only hidden tools) renders no row at all;
  // MessageList shows its typing indicator in the meantime, so the transcript never holds an empty bubble.
  if (!isUser && !hasToolParts && !hasReplyText(parts)) return null;

  // Text renders first, then the tool calls (status rows / product grid) beneath it, in one bubble —
  // enforced here rather than trusted to the caller's part order.
  const orderedParts = [...parts.filter((p) => p.type === "text"), ...parts.filter((p) => p.type !== "text")];

  return (
    <div className={cn("flex gap-3 px-4 py-3", isUser && "flex-row-reverse")}>
      <div
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-muted-foreground"
        )}
        aria-label={isUser ? "User" : "Assistant"}
      >
        {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>
      <div
        className={cn(
          "flex-1 min-w-0",
          isUser && "flex flex-col items-end"
        )}
      >
        <div
          className={cn(
            "rounded-2xl px-4",
            isUser
              ? "py-2.5 bg-primary text-primary-foreground max-w-[85%] inline-block"
              : hasToolParts
                ? "py-3 bg-muted w-full"
                : "py-3 bg-muted max-w-[85%] inline-block",
            !isUser && MARKDOWN_ASSISTANT_COLORS
          )}
        >
          <MessageParts parts={orderedParts} />
        </div>
      </div>
    </div>
  );
}
